import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { useInViewport } from 'ahooks';
import { useContext, useMemo, useRef } from 'react';
import { ApiQueryKey } from 'services/config/queryKey';
import { tradeList as getTradeList, TradeListParams } from 'services/api/conversation';
import type { DialogueReturnRefundCardMessage } from 'components/Dialogue/types';
import { MessageExtend } from '../MessageExtend';
import { MessageStatusDecorate } from '../MessageStatusDecorate';
import { useChatContext } from '../../contexts/ChatContext';
import { ItemImg } from 'components/ItemCard';
import { Divider } from 'antd';
import styled from '@emotion/styled';
import { formatNumber } from 'utils/numberFormat';
import { DialogueModeContext } from '../DialogueModeContext.tsx';

function ReturnRefundCardMsg({
  conversation,
  msg,
  raw,
}: {
  conversation: Conversation.Conversation;
  msg: DialogueReturnRefundCardMessage;
  raw: Conversation.Message;
}) {
  const { t } = useTranslate();

  const { dialogRef } = useChatContext();

  const ref = useRef<HTMLDivElement>(null);

  const [inViewPort = false] = useInViewport(ref, {
    root: dialogRef?.dom,
  });

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const { buyerId, storeId, tunnelAccountId, channel } = conversation;
  const {
    content: { orderId, skuId },
    raw: { failCause },
  } = msg;

  const queryParams = useMemo(
    () => ({
      internal_buyer_id: buyerId,
      internal_store_id: storeId,
      tunnelAccountId: tunnelAccountId,
      external_trade_ids: channel === 'TOKOPEDIA' ? undefined : orderId,
      invoice_number: channel === 'TOKOPEDIA' ? orderId : undefined,
      page_size: 1,
      page_no: 1,
    }),
    [buyerId, storeId, tunnelAccountId, channel, orderId],
  );
  const { status, data: trade } = useQuery({
    enabled: (inViewPort || isFaq) && !!queryParams,
    staleTime: 3 * 60 * 1000,
    queryKey: [ApiQueryKey.OrderCardTrade, queryParams],
    queryFn: ({ queryKey, signal }) =>
      getTradeList(queryKey[1] as TradeListParams, undefined, signal).then(
        (res) => res.tradeList?.[0],
      ),
  });

  const item = useMemo(() => {
    if (!trade) {
      return undefined;
    }
    return trade.items.find(({ externalSkuId }) => externalSkuId === skuId);
  }, [trade, skuId]);

  return (
    <div ref={ref}>
      {status === 'success' && trade && item && (
        <MessageExtend
          raw={raw}
          msg={msg}
          conversation={conversation}
          content={
            <MessageStatusDecorate
              message={raw}
              status={msg?.status}
              content={
                <Container>
                  <h4>
                    {t(
                      'scene.chatWorkspace.messageThread.returnRefundCard.action.requestReturnRefund',
                    )}
                  </h4>
                  <div>
                    <ItemImg
                      hasSmallPic="both"
                      src={item.itemPic}
                      alt="product"
                      target={dialogRef?.dom}
                    />
                    <section>
                      <p title={item.itemName}>{item.itemName}</p>
                      <div>
                        <span>
                          {trade.unit}
                          {formatNumber(item.unitPrice, '0.00')}
                        </span>
                        <Divider type="vertical" />
                        <span>{item.skuName}</span>
                      </div>
                    </section>
                  </div>
                </Container>
              }
              failCause={failCause}
              dialogRef={dialogRef}
            />
          }
        />
      )}
    </div>
  );
}

const Container = styled.div`
  width: 460px;
  padding: 8px;
  background: #fff;
  border-radius: 4px;
  > h4 {
    margin-bottom: 6px;
    font-size: 12px;
  }
  > div {
    display: flex;
    > * {
      flex: 0 0 auto;
    }
    > section {
      flex: 1 0 0;
      margin-left: 8px;
      overflow: hidden;
      > p {
        width: 100%;
        margin-bottom: 2px;
        color: #515359;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      > div {
        display: flex;
        align-items: center;
        font: 12px;
        color: #84868c;
      }
    }
  }
`;

export default ReturnRefundCardMsg;
